// v3.4.059 — نظام إرسال الدعوات
// ارفع تصميم الدعوة، حدّد مكان ظهور اسم المدعو عليه بالسحب، ثم أرسله لكل مدعو
// باسمه إلى مجموعة عملاء أو مسوقين أو لقائمة كاملة أو لملف Excel.
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  FiUpload, FiTrash2, FiSend, FiUsers, FiImage, FiDownload,
  FiRefreshCw, FiClock, FiCheckCircle, FiAlertCircle, FiEye, FiSave,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { invitations as invApi } from "../services/api";

const API_BASE = (typeof window !== "undefined" ? window.location.origin : ""); // v3.4.482 — نفس العنوان المفتوح (نطاق DO أو erp.rael.com.sa)

const CARD = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 16 };
const H = { margin: "0 0 12px", fontSize: 15, fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 8 };
const LBL = { display: "block", fontSize: 11.5, fontWeight: 700, color: "#4b5563", marginBottom: 4 };
const INP = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "inherit" };
const BTN = (bg, extra = {}) => ({ padding: "8px 16px", background: bg, color: "#fff", border: "none", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6, ...extra });

const fmtDate = (d) => (d ? new Date(d).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" }) : "—");

// خط Tajawal من الخادم نفسه حتى تطابق المعاينة الحيّة الصورة الناتجة
let fontInjected = false;
function useTajawal() {
  useEffect(() => {
    if (fontInjected) return;
    fontInjected = true;
    const st = document.createElement("style");
    st.textContent = `@font-face{font-family:'TajawalInv';src:url('${API_BASE}/api/invitations/font/tajawal') format('truetype');font-weight:700;font-display:swap;}`;
    document.head.appendChild(st);
  }, []);
}

export default function InvitationsTab({ user }) {
  useTajawal();
  const isAdmin = (user?.role === "admin" && !user?.impersonating);
  const canSend = isAdmin || !!user?.can_send_business_msg;

  const [designs, setDesigns] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("compose"); // compose | history

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await invApi.listDesigns();
      setDesigns(r.data.designs || []);
      setSelId((prev) => prev || r.data.designs?.[0]?.id || null);
    } catch (e) { toast.error(e.response?.data?.error || "تعذّر تحميل التصاميم"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = designs.find((d) => d.id === selId) || null;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["compose", "إنشاء وإرسال", <FiSend key="a" />], ["history", "سجل الإرسال", <FiClock key="b" />]].map(([k, l, ic]) => (
          <button key={k} onClick={() => setView(k)}
            style={{ ...BTN(view === k ? "#0f766e" : "#f3f4f6"), color: view === k ? "#fff" : "#4b5563" }}>
            {ic} {l}
          </button>
        ))}
      </div>

      {view === "history" ? <HistorySection /> : (
        <>
          <DesignsSection
            designs={designs} selId={selId} setSelId={setSelId}
            loading={loading} reload={load} canSend={canSend}
          />
          {selected && (
            <>
              <PlacementSection design={selected} canSend={canSend} onSaved={load} />
              <SendSection design={selected} canSend={canSend} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 1) التصاميم
// ═══════════════════════════════════════════════════════════════
function DesignsSection({ designs, selId, setSelId, loading, reload, canSend }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("حجم الصورة أكبر من 25 ميجا"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name.replace(/\.[^.]+$/, ""));
    try {
      const r = await invApi.uploadDesign(fd);
      toast.success("تم رفع التصميم — حدّد الآن مكان الاسم");
      await reload();
      setSelId(r.data.design.id);
    } catch (e) { toast.error(e.response?.data?.error || "تعذّر رفع التصميم"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (d) => {
    if (!window.confirm(`حذف التصميم «${d.name}»؟`)) return;
    try { await invApi.deleteDesign(d.id); toast.success("حُذف التصميم"); reload(); }
    catch (e) { toast.error(e.response?.data?.error || "تعذّر الحذف"); }
  };

  return (
    <div style={CARD}>
      <h3 style={H}><FiImage /> تصاميم الدعوات</h3>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        {loading && <div style={{ color: "#9ca3af", fontSize: 12 }}>جارٍ التحميل…</div>}
        {!loading && designs.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 12.5 }}>لا توجد تصاميم بعد — ارفع صورة الدعوة لتبدأ</div>
        )}
        {designs.map((d) => (
          <div key={d.id} onClick={() => setSelId(d.id)}
            style={{
              width: 150, cursor: "pointer", borderRadius: 10, overflow: "hidden",
              border: selId === d.id ? "3px solid #0f766e" : "1px solid #e5e7eb",
              boxShadow: selId === d.id ? "0 4px 14px rgba(15,118,110,.18)" : "none", background: "#f9fafb",
            }}>
            <img src={`${API_BASE}/api/invitations/designs/${d.id}/image`} alt={d.name}
              style={{ width: "100%", height: 130, objectFit: "cover", display: "block", background: "#e5e7eb" }} />
            <div style={{ padding: "6px 8px" }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
              <div style={{ fontSize: 10, color: "#9ca3af", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{d.width}×{d.height}</span>
                {canSend && <button onClick={(e) => { e.stopPropagation(); remove(d); }}
                  style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", padding: 2 }} title="حذف"><FiTrash2 size={12} /></button>}
              </div>
            </div>
          </div>
        ))}

        {canSend && (
          <div onClick={() => !busy && fileRef.current?.click()}
            style={{ width: 150, height: 165, border: "2px dashed #cbd5e1", borderRadius: 10, display: "flex",
              flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
              cursor: busy ? "wait" : "pointer", color: "#64748b", background: "#f8fafc" }}>
            <FiUpload size={22} />
            <span style={{ fontSize: 12, fontWeight: 700 }}>{busy ? "جارٍ الرفع…" : "رفع تصميم"}</span>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>PNG / JPG / WEBP</span>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
          onChange={(e) => upload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 2) مكان ظهور الاسم
// ═══════════════════════════════════════════════════════════════
function PlacementSection({ design, canSend, onSaved }) {
  const [cfg, setCfg] = useState(design);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const boxRef = useRef(null);

  useEffect(() => { setCfg(design); }, [design]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const sample = (cfg.sample_name || "").trim() || "اسم المدعو";

  // المعاينة الفعلية من الخادم — تتحدّث بعد توقّف التعديل
  useEffect(() => {
    const t = setTimeout(() => {
      const q = new URLSearchParams({
        name: sample, name_x: cfg.name_x, name_y: cfg.name_y, font_size: cfg.font_size,
        max_width: cfg.max_width, color: cfg.color || "#ffffff", align: cfg.align || "center",
        prefix: cfg.prefix || "", suffix: cfg.suffix || "", shadow: cfg.shadow ? "1" : "0",
        t: Date.now(),
      });
      setPreviewUrl(`${API_BASE}/api/invitations/designs/${design.id}/preview?${q}`);
    }, 500);
    return () => clearTimeout(t);
  }, [design.id, cfg, sample]);

  const move = (e) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const cx = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const cy = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    setCfg((c) => ({
      ...c,
      name_x: Math.min(1, Math.max(0, cx / r.width)).toFixed(4),
      name_y: Math.min(1, Math.max(0, cy / r.height)).toFixed(4),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await invApi.updateDesign(design.id, {
        name: cfg.name, name_x: cfg.name_x, name_y: cfg.name_y, font_size: cfg.font_size,
        max_width: cfg.max_width, color: cfg.color, align: cfg.align, prefix: cfg.prefix || "",
        suffix: cfg.suffix || "", shadow: !!cfg.shadow, sample_name: cfg.sample_name || "",
        caption: cfg.caption || "",
      });
      toast.success("حُفظ مكان الاسم");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.error || "تعذّر الحفظ"); }
    finally { setSaving(false); }
  };

  const ratio = design.height && design.width ? design.height / design.width : 1.4;
  const overlayFont = `${Number(cfg.font_size || 0.045) * 100}cqh`;

  return (
    <div style={CARD}>
      <h3 style={H}>🎯 مكان ظهور اسم المدعو</h3>
      <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 12 }}>
        اسحب الاسم على التصميم لتحديد مكانه، ثم اضبط الحجم واللون. المعاينة على اليسار هي الصورة الفعلية التي ستُرسل.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(240px,1fr) minmax(240px,1fr) 230px", gap: 14, alignItems: "start" }}>
        {/* لوحة السحب */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4b5563", marginBottom: 5 }}>اسحب لتحديد المكان</div>
          <div ref={boxRef}
            onMouseDown={(e) => { setDragging(true); move(e); }}
            onMouseMove={(e) => dragging && move(e)}
            onMouseUp={() => setDragging(false)}
            onMouseLeave={() => setDragging(false)}
            onTouchStart={(e) => { setDragging(true); move(e); }}
            onTouchMove={(e) => { if (dragging) { e.preventDefault(); move(e); } }}
            onTouchEnd={() => setDragging(false)}
            style={{
              position: "relative", width: "100%", paddingTop: `${ratio * 100}%`,
              border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden",
              cursor: dragging ? "grabbing" : "crosshair", containerType: "size", background: "#f3f4f6",
              userSelect: "none", touchAction: "none",
            }}>
            <img src={`${API_BASE}/api/invitations/designs/${design.id}/image`} alt=""
              draggable={false}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", pointerEvents: "none" }} />
            <div style={{
              position: "absolute", top: `${Number(cfg.name_y) * 100}%`, left: `${Number(cfg.name_x) * 100}%`,
              transform: cfg.align === "right" ? "translate(0,-50%)" : cfg.align === "left" ? "translate(-100%,-50%)" : "translate(-50%,-50%)",
              color: cfg.color || "#fff", fontFamily: "'TajawalInv', 'Segoe UI', Tahoma, sans-serif",
              fontWeight: 700, fontSize: overlayFont, whiteSpace: "nowrap", pointerEvents: "none",
              textShadow: cfg.shadow ? "0 2px 8px rgba(0,0,0,.5)" : "none", direction: "rtl", lineHeight: 1,
            }}>
              {(cfg.prefix || "") + sample + (cfg.suffix || "")}
            </div>
            {/* خطوط إرشادية */}
            <div style={{ position: "absolute", top: `${Number(cfg.name_y) * 100}%`, left: 0, right: 0, height: 1, background: "rgba(15,118,110,.45)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", left: `${Number(cfg.name_x) * 100}%`, top: 0, bottom: 0, width: 1, background: "rgba(15,118,110,.45)", pointerEvents: "none" }} />
          </div>
        </div>

        {/* المعاينة الفعلية */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#4b5563", marginBottom: 5, display: "flex", alignItems: "center", gap: 5 }}>
            <FiEye size={12} /> المعاينة الفعلية (كما ستُرسل)
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#f3f4f6", minHeight: 120 }}>
            {previewUrl ? <img src={previewUrl} alt="معاينة" style={{ width: "100%", display: "block" }} /> : null}
          </div>
          {previewUrl && (
            <a href={previewUrl + "&download=1"} target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 7, fontSize: 11.5, color: "#0f766e", fontWeight: 700, textDecoration: "none" }}>
              <FiDownload size={12} /> تنزيل المعاينة
            </a>
          )}
        </div>

        {/* الضبط */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div>
            <label style={LBL}>اسم التصميم</label>
            <input value={cfg.name || ""} onChange={(e) => set("name", e.target.value)} style={INP} />
          </div>
          <div>
            <label style={LBL}>اسم تجريبي للمعاينة</label>
            <input value={cfg.sample_name || ""} onChange={(e) => set("sample_name", e.target.value)} placeholder="محمد عبدالله" style={INP} />
          </div>
          <div>
            <label style={LBL}>حجم الخط — {(Number(cfg.font_size) * 100).toFixed(1)}% من ارتفاع الصورة</label>
            <input type="range" min="0.01" max="0.16" step="0.002" value={cfg.font_size}
              onChange={(e) => set("font_size", e.target.value)} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={LBL}>أقصى عرض للاسم — {(Number(cfg.max_width) * 100).toFixed(0)}%</label>
            <input type="range" min="0.2" max="1" step="0.02" value={cfg.max_width}
              onChange={(e) => set("max_width", e.target.value)} style={{ width: "100%" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={LBL}>اللون</label>
              <input type="color" value={cfg.color || "#ffffff"} onChange={(e) => set("color", e.target.value)}
                style={{ width: "100%", height: 34, border: "1.5px solid #e5e7eb", borderRadius: 8, cursor: "pointer", background: "#fff" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={LBL}>المحاذاة</label>
              <select value={cfg.align || "center"} onChange={(e) => set("align", e.target.value)} style={{ ...INP, appearance: "auto" }}>
                <option value="center">توسيط</option>
                <option value="right">يمين</option>
                <option value="left">يسار</option>
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={LBL}>قبل الاسم</label>
              <input value={cfg.prefix || ""} onChange={(e) => set("prefix", e.target.value)} placeholder="الأستاذ/ " style={INP} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={LBL}>بعد الاسم</label>
              <input value={cfg.suffix || ""} onChange={(e) => set("suffix", e.target.value)} style={INP} />
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={!!cfg.shadow} onChange={(e) => set("shadow", e.target.checked)} />
            ظل خفيف خلف الاسم (لتوضيحه على الخلفيات الفاتحة)
          </label>
          {canSend && (
            <button onClick={save} disabled={saving} style={BTN(saving ? "#94a3b8" : "#0f766e", { justifyContent: "center" })}>
              <FiSave size={13} /> {saving ? "جارٍ الحفظ…" : "حفظ الإعدادات"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 3) المستلمون + الإرسال
// ═══════════════════════════════════════════════════════════════
function SendSection({ design, canSend }) {
  const [sources, setSources] = useState({ client_groups: [], marketer_groups: [], all: {} });
  const [srcType, setSrcType] = useState("client_group");
  const [srcId, setSrcId] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loadingRows, setLoadingRows] = useState(false);
  const [manual, setManual] = useState("");
  const fileRef = useRef(null);

  const [session, setSession] = useState("corporate");
  const [caption, setCaption] = useState(design.caption || "");
  const [delayMs, setDelayMs] = useState(4000);
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false); // v3.4.089 — تصدير كل الدعوات ZIP
  const [progress, setProgress] = useState(null); // {done,total,sent,failed}
  const [results, setResults] = useState({});     // index -> {status,error}
  const abortRef = useRef(null);

  useEffect(() => { setCaption(design.caption || ""); }, [design.id, design.caption]);

  useEffect(() => {
    invApi.audienceSources().then((r) => {
      setSources(r.data);
      if (!srcId && r.data.client_groups?.length) setSrcId(String(r.data.client_groups[0].id));
    }).catch(() => {});
  }, []);

  const applyRows = (list) => {
    setRows(list);
    setPicked(new Set(list.map((_, i) => i).filter((i) => list[i].valid)));
    setResults({});
    setProgress(null);
  };

  const loadAudience = async () => {
    const needsId = srcType === "client_group" || srcType === "marketer_group";
    if (needsId && !srcId) { toast.error("اختر المجموعة"); return; }
    setLoadingRows(true);
    try {
      const r = await invApi.audience({ type: srcType, id: needsId ? srcId : undefined });
      applyRows(r.data.rows || []);
      toast.success(`تم جلب ${r.data.rows?.length || 0} مستلمًا`);
    } catch (e) { toast.error(e.response?.data?.error || "تعذّر جلب المستلمين"); }
    finally { setLoadingRows(false); }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoadingRows(true);
    try {
      const r = await invApi.parseExcel(fd);
      applyRows(r.data.rows || []);
      toast.success(`قُرئ ${r.data.rows?.length || 0} صفًا`);
    } catch (e) { toast.error(e.response?.data?.error || "تعذّرت قراءة الملف"); }
    finally { setLoadingRows(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addManual = () => {
    const parsed = manual.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const parts = l.split(/[,\t;|]+/).map((x) => x.trim());
      const phoneIdx = parts.findIndex((x) => /\d{6,}/.test(x));
      const phoneRaw = phoneIdx >= 0 ? parts[phoneIdx] : "";
      const name = parts.filter((_, i) => i !== phoneIdx).join(" ").trim();
      let p = phoneRaw.replace(/\D/g, "");
      if (p.startsWith("00")) p = p.slice(2);
      if (p.startsWith("0")) p = "966" + p.slice(1);
      if (p.length === 9 && p.startsWith("5")) p = "966" + p;
      return { name, phone: p, valid: /^966\d{9}$/.test(p) };
    });
    if (!parsed.length) { toast.error("اكتب سطرًا لكل مدعو: الاسم، الجوال"); return; }
    applyRows([...rows, ...parsed]);
    setManual("");
  };

  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const editName = (i, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, name: v } : x)));

  const chosen = useMemo(() => rows.map((r, i) => ({ ...r, i })).filter((r) => picked.has(r.i)), [rows, picked]);
  const invalidCount = rows.filter((r) => !r.valid).length;

  const send = async () => {
    if (!chosen.length) { toast.error("لم تختر أي مستلم"); return; }
    if (!window.confirm(`إرسال الدعوة إلى ${chosen.length} مستلمًا عبر ${session === "corporate" ? "رقم الشركة" : "رقم المسوقين"}؟`)) return;
    setSending(true);
    setResults({});
    setProgress({ total: chosen.length, sent: 0, failed: 0 });
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API_BASE}/api/invitations/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({
          design_id: design.id, session, caption, delayMs: Number(delayMs) || 0,
          rows: chosen.map((r) => ({ name: r.name, phone: r.phone })),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let sent = 0, failed = 0;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.trim()) continue;
          let o; try { o = JSON.parse(line); } catch { continue; }
          if (o.done) { setProgress({ total: chosen.length, sent: o.sent, failed: o.failed, finished: true }); continue; }
          if (o.status) {
            const rowIndex = chosen[o.i]?.i;
            if (o.status === "sent") sent++; else failed++;
            setResults((p) => ({ ...p, [rowIndex]: { status: o.status, error: o.error } }));
            setProgress({ total: chosen.length, sent, failed });
          }
        }
      }
      toast.success("انتهى الإرسال");
    } catch (e) {
      if (e.name === "AbortError") toast("أُوقف الإرسال");
      else toast.error(e.message || "تعذّر الإرسال");
    } finally { setSending(false); abortRef.current = null; }
  };

  // v3.4.089 — تصدير كل الدعوات المختارة كملف ZIP (صورة PNG لكل مستلم باسمه)
  const exportAll = async () => {
    if (!chosen.length) { toast.error("لم تختر أي مستلم"); return; }
    setExporting(true);
    const t = toast.loading(`جارٍ تجهيز ${chosen.length} دعوة…`);
    try {
      const res = await fetch(`${API_BASE}/api/invitations/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ design_id: design.id, rows: chosen.map((r) => ({ name: r.name, phone: r.phone })) }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(design.name || "invitations").replace(/[\\/:*?"<>|]/g, " ").trim()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`تم تصدير ${chosen.length} دعوة`, { id: t });
    } catch (e) {
      toast.error(e.message || "تعذّر التصدير", { id: t });
    } finally { setExporting(false); }
  };

  const exportCsv = () => {
    const head = "الاسم,الجوال,الحالة,الخطأ\n";
    const body = rows.map((r, i) => {
      const st = results[i]?.status || "";
      return [r.name, r.phone, st === "sent" ? "أُرسلت" : st === "failed" ? "فشل" : "", results[i]?.error || ""]
        .map((x) => `"${String(x || "").replace(/"/g, '""')}"`).join(",");
    }).join("\n");
    const blob = new Blob(["﻿" + head + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `invitations-${design.name}.csv`;
    a.click();
  };

  const retryFailed = () => {
    const failedIdx = Object.entries(results).filter(([, v]) => v.status === "failed").map(([k]) => Number(k));
    if (!failedIdx.length) { toast.error("لا توجد رسائل فاشلة"); return; }
    setPicked(new Set(failedIdx));
    toast.success(`حُدّد ${failedIdx.length} مستلمًا فاشلًا — اضغط إرسال`);
  };

  const SRC = [
    ["client_group", "مجموعة عملاء"],
    ["marketer_group", "مجموعة مسوقين"],
    ["clients", `كل العملاء (${sources.all?.clients || 0})`],
    ["marketers", `كل المسوقين (${sources.all?.marketers || 0})`],
    ["developers", `المطورون (${sources.all?.developers || 0})`],
  ];

  return (
    <>
      <div style={CARD}>
        <h3 style={H}><FiUsers /> المستلمون</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <div style={{ minWidth: 170 }}>
            <label style={LBL}>المصدر</label>
            <select value={srcType} onChange={(e) => setSrcType(e.target.value)} style={{ ...INP, appearance: "auto" }}>
              {SRC.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {(srcType === "client_group" || srcType === "marketer_group") && (
            <div style={{ minWidth: 200 }}>
              <label style={LBL}>المجموعة</label>
              <select value={srcId} onChange={(e) => setSrcId(e.target.value)} style={{ ...INP, appearance: "auto" }}>
                <option value="">— اختر —</option>
                {(srcType === "client_group" ? sources.client_groups : sources.marketer_groups).map((g) => (
                  <option key={g.id} value={g.id}>{g.name} ({g.members})</option>
                ))}
              </select>
            </div>
          )}
          <button onClick={loadAudience} disabled={loadingRows} style={BTN("#0f766e")}>
            <FiRefreshCw size={13} /> {loadingRows ? "جارٍ…" : "جلب المستلمين"}
          </button>
          <button onClick={() => fileRef.current?.click()} style={BTN("#475569")}>
            <FiUpload size={13} /> من ملف Excel
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => uploadExcel(e.target.files?.[0])} />
          {rows.length > 0 && <button onClick={() => applyRows([])} style={BTN("#e5e7eb", { color: "#374151" })}>مسح القائمة</button>}
        </div>

        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 12, color: "#0f766e", cursor: "pointer", fontWeight: 700 }}>إضافة يدوية</summary>
          <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea value={manual} onChange={(e) => setManual(e.target.value)} rows={3}
              placeholder={"سطر لكل مدعو:\nمحمد عبدالله, 0551234567"}
              style={{ ...INP, flex: 1, fontFamily: "inherit" }} />
            <button onClick={addManual} style={BTN("#0f766e")}>إضافة</button>
          </div>
        </details>

        {rows.length > 0 && (
          <>
            <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <b>{rows.length}</b> مستلمًا • محدَّد <b style={{ color: "#0f766e" }}>{picked.size}</b>
              {invalidCount > 0 && <span style={{ color: "#b45309" }}>⚠ {invalidCount} رقمًا غير صالح</span>}
              <button onClick={() => setPicked(new Set(rows.map((_, i) => i).filter((i) => rows[i].valid)))}
                style={{ ...BTN("#f3f4f6", { color: "#374151", padding: "4px 10px", fontSize: 11 }) }}>تحديد الصالح</button>
              <button onClick={() => setPicked(new Set())}
                style={{ ...BTN("#f3f4f6", { color: "#374151", padding: "4px 10px", fontSize: 11 }) }}>إلغاء التحديد</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead style={{ position: "sticky", top: 0, background: "#f9fafb" }}>
                  <tr>
                    <th style={{ padding: 7, width: 34 }}></th>
                    <th style={{ padding: 7, textAlign: "right" }}>الاسم (كما سيظهر على الدعوة)</th>
                    <th style={{ padding: 7, textAlign: "right", width: 130 }}>الجوال</th>
                    <th style={{ padding: 7, textAlign: "right", width: 120 }}>الحالة</th>
                    <th style={{ padding: 7, width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const st = results[i];
                    return (
                      <tr key={i} style={{ borderTop: "1px solid #f1f5f9", background: !r.valid ? "#fffbeb" : "#fff" }}>
                        <td style={{ padding: 5, textAlign: "center" }}>
                          <input type="checkbox" checked={picked.has(i)} onChange={() => toggle(i)} />
                        </td>
                        <td style={{ padding: 4 }}>
                          <input value={r.name} onChange={(e) => editName(i, e.target.value)}
                            style={{ width: "100%", border: "1px solid transparent", background: "transparent", padding: "3px 5px", borderRadius: 5, fontSize: 12, fontFamily: "inherit" }}
                            onFocus={(e) => (e.target.style.border = "1px solid #cbd5e1")}
                            onBlur={(e) => (e.target.style.border = "1px solid transparent")} />
                        </td>
                        <td style={{ padding: 7, direction: "ltr", textAlign: "right", color: r.valid ? "#374151" : "#b45309" }}>{r.phone || "—"}</td>
                        <td style={{ padding: 7 }}>
                          {st?.status === "sent" && <span style={{ color: "#059669", fontWeight: 700 }}><FiCheckCircle size={11} /> أُرسلت</span>}
                          {st?.status === "failed" && <span style={{ color: "#dc2626", fontWeight: 700 }} title={st.error}><FiAlertCircle size={11} /> {st.error || "فشل"}</span>}
                          {!st && !r.valid && <span style={{ color: "#b45309" }}>رقم غير صالح</span>}
                        </td>
                        <td style={{ padding: 4, textAlign: "center" }}>
                          <a href={`${API_BASE}/api/invitations/designs/${design.id}/preview?name=${encodeURIComponent(r.name || "")}&download=1`}
                            target="_blank" rel="noreferrer" title="تنزيل دعوته"
                            style={{ color: "#0f766e" }}><FiDownload size={12} /></a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={CARD}>
        <h3 style={H}><FiSend /> الإرسال</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
          <div style={{ minWidth: 170 }}>
            <label style={LBL}>يُرسل من</label>
            <select value={session} onChange={(e) => setSession(e.target.value)} style={{ ...INP, appearance: "auto" }}>
              <option value="corporate">رقم الشركة (العملاء)</option>
              <option value="marketers">رقم المسوقين</option>
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <label style={LBL}>الفاصل بين الرسائل (ملّي ثانية)</label>
            <input value={delayMs} onChange={(e) => setDelayMs(e.target.value)} inputMode="numeric" dir="ltr" style={INP} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={LBL}>نص الرسالة المرافقة للصورة</label>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} style={{ ...INP, fontFamily: "inherit" }}
            placeholder="يسرّنا دعوتكم لحضور…" />
        </div>

        {progress && (
          <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, marginBottom: 10, fontSize: 12 }}>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontWeight: 700 }}>
              <span>الإجمالي: {progress.total}</span>
              <span style={{ color: "#059669" }}>أُرسلت: {progress.sent}</span>
              <span style={{ color: "#dc2626" }}>فشل: {progress.failed}</span>
              {progress.finished && <span style={{ color: "#0f766e" }}>✔ اكتمل</span>}
            </div>
            <div style={{ height: 6, background: "#e5e7eb", borderRadius: 4, marginTop: 7, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round(((progress.sent + progress.failed) / Math.max(1, progress.total)) * 100)}%`, background: "#0f766e", transition: "width .2s" }} />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canSend && !sending && (
            <button onClick={send} disabled={!chosen.length} style={BTN(chosen.length ? "#059669" : "#94a3b8")}>
              <FiSend size={13} /> إرسال إلى {chosen.length} مستلمًا
            </button>
          )}
          {sending && (
            <button onClick={() => abortRef.current?.abort()} style={BTN("#dc2626")}>إيقاف الإرسال</button>
          )}
          <button onClick={exportAll} disabled={!chosen.length || exporting || sending} style={BTN(chosen.length && !exporting ? "#0f766e" : "#94a3b8")}>
            <FiDownload size={13} /> {exporting ? "جارٍ التصدير…" : `تصدير كل الدعوات (${chosen.length})`}
          </button>
          {Object.keys(results).length > 0 && (
            <>
              <button onClick={retryFailed} style={BTN("#f59e0b")}>تحديد الفاشل لإعادة الإرسال</button>
              <button onClick={exportCsv} style={BTN("#475569")}><FiDownload size={13} /> تصدير النتائج</button>
            </>
          )}
        </div>
        {!canSend && <div style={{ fontSize: 12, color: "#b45309", marginTop: 8 }}>ليس لديك صلاحية الإرسال — يمكنك المعاينة والتنزيل فقط.</div>}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// 4) السجل
// ═══════════════════════════════════════════════════════════════
function HistorySection() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(null);
  const [sends, setSends] = useState([]);

  useEffect(() => { invApi.batches().then((r) => setBatches(r.data.batches || [])).catch(() => {}); }, []);

  const openBatch = async (b) => {
    if (open === b.id) { setOpen(null); return; }
    setOpen(b.id);
    try { const r = await invApi.batch(b.id); setSends(r.data.sends || []); } catch { setSends([]); }
  };

  return (
    <div style={CARD}>
      <h3 style={H}><FiClock /> سجل إرسال الدعوات</h3>
      {batches.length === 0 && <div style={{ fontSize: 12.5, color: "#6b7280" }}>لا توجد عمليات إرسال بعد</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {batches.map((b) => (
          <div key={b.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <div onClick={() => openBatch(b)} style={{ padding: "9px 12px", cursor: "pointer", display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", fontSize: 12, background: open === b.id ? "#f0fdfa" : "#fff" }}>
              <b style={{ color: "#111827" }}>{b.design_name || "—"}</b>
              <span style={{ color: "#6b7280" }}>{fmtDate(b.created_at)}</span>
              <span style={{ color: "#6b7280" }}>{b.session === "corporate" ? "رقم الشركة" : "رقم المسوقين"}</span>
              <span>الإجمالي {b.total}</span>
              <span style={{ color: "#059669", fontWeight: 700 }}>أُرسلت {b.sent}</span>
              {b.failed > 0 && <span style={{ color: "#dc2626", fontWeight: 700 }}>فشل {b.failed}</span>}
              <span style={{ marginInlineStart: "auto", color: "#9ca3af" }}>{b.created_by_name || ""}</span>
            </div>
            {open === b.id && (
              <div style={{ maxHeight: 260, overflowY: "auto", borderTop: "1px solid #e5e7eb" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                  <tbody>
                    {sends.map((s, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ padding: 6 }}>{s.name}</td>
                        <td style={{ padding: 6, direction: "ltr", textAlign: "right" }}>{s.phone}</td>
                        <td style={{ padding: 6, color: s.status === "sent" ? "#059669" : "#dc2626", fontWeight: 700 }}>
                          {s.status === "sent" ? "أُرسلت" : s.error || "فشل"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
